import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EXHIBITION_DELETE_CONFIRM_PASSWORD } from '../config/exhibitionDeletePassword';

export interface ExhibitionDeletePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onVerified: () => void | Promise<void>;
}

export const ExhibitionDeletePasswordDialog: React.FC<ExhibitionDeletePasswordDialogProps> = ({
  open,
  onClose,
  onVerified
}) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setLocalError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (password.trim() !== EXHIBITION_DELETE_CONFIRM_PASSWORD) {
      setLocalError(t('exhibition.deletePasswordWrong', 'Incorrect password.'));
      return;
    }
    setLocalError(null);
    setSubmitting(true);
    try {
      await Promise.resolve(onVerified());
    } catch {
      // Parent may set global error; keep dialog open
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) {
          onClose();
        }
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('exhibition.deletePasswordTitle', 'Confirm deletion')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('exhibition.deletePasswordDescription', 'Enter the deletion password to continue.')}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          type="password"
          label={t('exhibition.deletePasswordLabel', 'Password')}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setLocalError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleConfirm();
            }
          }}
          error={Boolean(localError)}
          helperText={localError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button onClick={() => void handleConfirm()} variant="contained" color="error" disabled={submitting}>
          {t('common.delete', 'Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
